import { useCallback, useEffect, useReducer, useState } from "react";
import { Button, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate, useParams, useLoaderData } from "react-router-dom";
import { getUserId, checkIfCurrentUserIsAdmin } from "../utils/AuthUtils";
import { getAvailableECSStatuses } from '../utils/ECSUtils';
import Alerts from './components/alerts/Alerts';
import Details from './components/details/Details';
import Header from './components/header/Header';
import NotesContainer from './components/notes/NotesContainer';
import './components/notes/styles.scss';
import useAlertFading from './hooks/useAlertFading';
import { useQuestionnaireTrackingAPI } from './hooks/useQuestionnaireTrackingAPI';
import {
    initialState as dataInitialState, dataReducer, SET_AVAILABLE_STATUSES,
    SET_PARTICIPANT_METADATA, SET_SUBMISSION_RECORD, SET_TRACKING_RECORD, UPDATE_TRACKING_RECORD
} from './reducers/dataReducer';
import { SET_ERROR, SET_LOADING, SET_SUCCESS, initialState as statusInitialState, statusReducer } from './reducers/statusReducer';
import './styles.scss';
import { useStateStore } from "../AppState";

const SurveyQuestionnaireDetailsConsole = () => {
    const { companyId, participantId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const loaderData = useLoaderData();
    const [status, dispatch] = useReducer(statusReducer, statusInitialState);
    const [data, dataDispatch] = useReducer(dataReducer, {
        ...dataInitialState,
        trackingRecord: loaderData.trackingRecord,
        participantMetadata: loaderData.participantMetadata,
        availableStatuses: loaderData.availableStatuses,
        submissionRecord: loaderData.submissionRecord
    });

    const [userId, setUserId] = useState("");
    const [fadingAlerts, setFadingAlerts] = useState({
        statusUpdateSuccess: false,
        statusUpdateError: false,
        displaySuccess: false,
        displayError: false,
        displayDetailsSearchError: false
    });
    const [isAdminUser, setIsAdminUser] = useState(false);
    const { setLoadingStatus } = useStateStore();

    const searchParams = new URLSearchParams(location.search);
    const surveyYear = searchParams.get('surveyYear');

    const {
        fetchQuestionnaireRecord,
        fetchParticipantMetadata,
        updateTrackingRecordStatus,
        getSubmissionFile,
        getProcessedFile,
        fetchLatestSubmissionRecord
    } = useQuestionnaireTrackingAPI(surveyYear, companyId, participantId);

    useAlertFading(status.success.statusUpdate,
        (value) => dispatch({ type: SET_SUCCESS, payload: { statusUpdate: value } }),
        'statusUpdateSuccess',
        setFadingAlerts
    );
    useAlertFading(status.error.display,
        (value) => dispatch({ type: SET_ERROR, payload: { display: value } }),
        'displayError',
        setFadingAlerts
    );
    useAlertFading(status.error.displayDetailsSearch,
        (value) => dispatch({ type: SET_ERROR, payload: { displayDetailsSearch: value } }),
        'displayDetailsSearchError',
        setFadingAlerts
    );

    const handleFetchQuestionnaireRecord = useCallback(async () => {
        const result = await fetchQuestionnaireRecord();
        if (result.success) {
            dataDispatch({ type: SET_TRACKING_RECORD, payload: result.data });
        } else {
            dispatch({
                type: SET_ERROR,
                payload: {
                    detailsSearch: result.error,
                    displayDetailsSearch: true
                }
            });
        }
        return result.success;
    }, [fetchQuestionnaireRecord, dataDispatch]);

    const handleFetchParticipantMetadata = useCallback(async () => {
        const result = await fetchParticipantMetadata();
        if (result.success) {
            dataDispatch({ type: SET_PARTICIPANT_METADATA, payload: result.data });
        }
        return result.success;
    }, [fetchParticipantMetadata, dataDispatch]);

    const handleUpdateTrackingRecordStatus = async () => {
        try {
            dispatch({ type: SET_LOADING, payload: { key: 'processingStatus', value: true } });
            dispatch({ type: SET_ERROR, payload: { display: false } });
            dispatch({ type: SET_SUCCESS, payload: { statusUpdate: false } });

            const result = await updateTrackingRecordStatus(data.trackingRecord);
            if (result.success) {
                dispatch({ type: SET_SUCCESS, payload: { statusUpdate: true, message: "Questionnaire details updated successfully" } });
            } else {
                dispatch({ type: SET_ERROR, payload: { display: true, message: result.error || "Failed to update questionnaire details" } });
                dispatch({ type: SET_SUCCESS, payload: { statusUpdate: false } });
            }
        } finally {
            dispatch({ type: SET_LOADING, payload: { key: 'processingStatus', value: false } });
        }
    };

    const handleChangeProcessingStatus = (value) => {
        dataDispatch({
            type: UPDATE_TRACKING_RECORD,
            payload: { processingStatus: value }
        });
    };

    const handleChangeAssignedTo = (value) => {
        dataDispatch({
            type: UPDATE_TRACKING_RECORD,
            payload: { assignedTo: value }
        });
    };

    const handleGoBack = () => {
        const queryParams = new URLSearchParams();
        if (loaderData.processingStatus) {
            queryParams.set('status', loaderData.processingStatus);
        }
        if (loaderData.sensitive !== undefined && loaderData.sensitive !== null) {
            queryParams.set('sensitive', loaderData.sensitive);
        }

        const queryString = queryParams.toString();
        const path = '/survey-questionnaire-submission' + (queryString ? `?${queryString}` : '');
        navigate(path, {
            replace: true,
            preventScrollReset: true
        });
    };

    const handleDownloadFile = (url, fileName = undefined) => {
        const link = document.createElement('a');
        link.href = url;
        if (fileName) {
            link.download = fileName;
        }
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    const handleGetSubmissionFile = async (fileKey) => {
        if (!isAdminUser && !fileKey) return;

        dispatch({ type: SET_LOADING, payload: { key: 'data', value: true } });
        const result = await getSubmissionFile(fileKey);
        if (result.success) {
            handleDownloadFile(result.data.url);
        } else {
            dispatch({ type: SET_ERROR, payload: { display: true, message: result.error } });
        }
        dispatch({ type: SET_LOADING, payload: { key: 'data', value: false } });
    }

    const handleGetProcessedFile = async (processedFileKey) => {
        if (!isAdminUser && !processedFileKey) return;

        dispatch({ type: SET_LOADING, payload: { key: 'data', value: true } });
        const result = await getProcessedFile(processedFileKey);
        if (result.success) {
            handleDownloadFile(result.data.url);
        } else {
            dispatch({ type: SET_ERROR, payload: { display: true, message: result.error } });
        }
        dispatch({ type: SET_LOADING, payload: { key: 'data', value: false } });
    }

    const handleFetchLatestSubmissionRecord = useCallback(async () => {
        if (!isAdminUser) return;

        dispatch({ type: SET_LOADING, payload: { key: 'data', value: true } });
        const result = await fetchLatestSubmissionRecord();
        if (result.success) {
            dataDispatch({ type: SET_SUBMISSION_RECORD, payload: result.data });
        } else {
            dispatch({ type: SET_ERROR, payload: { display: true, message: result.error } });
        }
        dispatch({ type: SET_LOADING, payload: { key: 'data', value: false } });
    }, [fetchLatestSubmissionRecord, dataDispatch, isAdminUser]);

    useEffect(() => {
        let isMounted = true;

        if (isMounted) {
            const initializeData = async () => {
                try {
                    const userId = await getUserId();
                    setUserId(userId);
                    const isAdmin = await checkIfCurrentUserIsAdmin();
                    setIsAdminUser(isAdmin);
                    setLoadingStatus(false);
                } catch (error) {
                    console.error('Error during initialization:', error);
                }
            };

            initializeData();
        }

        return () => {
            isMounted = false;
        };
    }, [setLoadingStatus]);

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            if (!companyId || !surveyYear || !userId) return;

            dispatch({ type: SET_LOADING, payload: { key: 'data', value: true } });
            dispatch({ type: SET_ERROR, payload: { display: false, detailsSearch: "", displayDetailsSearch: false } });

            try {
                // Fetch all initial data in parallel
                const [questionnaireSuccess, metadataSuccess, statuses] = await Promise.all([
                    handleFetchQuestionnaireRecord(),
                    handleFetchParticipantMetadata(),
                    getAvailableECSStatuses()
                ]);

                if (!isMounted) return;

                if (!questionnaireSuccess || !metadataSuccess) {
                    dispatch({ type: SET_ERROR, payload: { display: true, message: "Failed to fetch questionnaire record or participant metadata" } });
                }

                dataDispatch({ type: SET_AVAILABLE_STATUSES, payload: statuses });
            } catch (error) {
                console.error('Error fetching data:', error);
                if (isMounted) {
                    dispatch({ type: SET_ERROR, payload: { display: true, message: "Failed to fetch questionnaire record or participant metadata" } });
                }
            } finally {
                if (isMounted) {
                    dispatch({ type: SET_LOADING, payload: { key: 'data', value: false } });
                }
            }
        };

        if (userId) {
            fetchData();
        }

        return () => {
            isMounted = false;
        };
    }, [surveyYear, companyId, userId, handleFetchQuestionnaireRecord, handleFetchParticipantMetadata]);

    useEffect(() => {
        if (isAdminUser && participantId) {
            handleFetchLatestSubmissionRecord();
        }
    }, [handleFetchLatestSubmissionRecord, participantId, isAdminUser]);

    const handleCloseError = () => {
        dispatch({ type: SET_ERROR, payload: { display: false } });
    };

    const handleCloseSuccess = () => {
        dispatch({ type: SET_SUCCESS, payload: { statusUpdate: false } });
    };

    const handleCloseDetailsSearchError = () => {
        dispatch({ type: SET_ERROR, payload: { displayDetailsSearch: false } });
    };

    return (
        <div className="questionnaire-tracking-container">
            <Header
                trackingRecord={data.trackingRecord}
                isAdminUser={isAdminUser}
                submissionRecord={data.submissionRecord}
                onDownloadSubmission={handleGetSubmissionFile}
                onDownloadProcessed={handleGetProcessedFile}
            />
            <div className='divider'></div>

            {status.loading.data ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : (
                <div>
                    <Alerts
                        error={status.error}
                        success={status.success}
                        fadingAlerts={fadingAlerts}
                        onCloseError={handleCloseError}
                        onCloseSuccess={handleCloseSuccess}
                        onCloseDetailsSearchError={handleCloseDetailsSearchError}
                    />

                    {data.trackingRecord && Object.keys(data.trackingRecord).length > 0 && (
                        <Details
                            trackingRecord={data.trackingRecord}
                            submissionRecord={data.submissionRecord}
                            participantMetadata={data.participantMetadata}
                            availableStatuses={data.availableStatuses}
                            loading={status.loading.processingStatus}
                            onAssignedToChange={handleChangeAssignedTo}
                            onProcessingStatusChange={handleChangeProcessingStatus}
                            onSave={handleUpdateTrackingRecordStatus}
                        />
                    )}

                    <div className="questionnaire-notes">
                        <NotesContainer
                            surveyYear={surveyYear}
                            companyId={companyId}
                            userId={userId}
                            participantName={data.trackingRecord?.participantName || ''}
                            onError={(error) => dispatch({ type: SET_ERROR, payload: { display: true, message: error } })}
                        />
                    </div>

                    <Button
                        variant="secondary"
                        disabled={status.loading.data}
                        onClick={handleGoBack}
                        className="mt-4"
                    >
                        Go Back
                    </Button>
                </div>
            )}
        </div>
    );
};

export default SurveyQuestionnaireDetailsConsole;